import { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Plus, X, Edit2, FileText, Printer, Download, Calendar, MessageSquare } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import api, { API_URL } from "../lib/api";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleDateString("en-GB"); } catch { return iso; }
}

export default function KanbanBoard({
  boardKey,              // "accounting" | "cm_accounts"
  columns,
  setColumns,
  cards,
  statusField,           // "accounting_status" | "cm_status"
  onStatusChange,        // (cardId, newStatusName) => Promise
  onCardClick,           // (card) => void
  canAddColumn = true,
  emptyHint,
}) {
  const [adding, setAdding] = useState(false);
  const [newColName, setNewColName] = useState("");
  const [editingColId, setEditingColId] = useState(null);
  const [editName, setEditName] = useState("");

  // Group cards by column name
  const cardsByColumn = {};
  columns.forEach((c) => { cardsByColumn[c.name] = []; });
  cards.forEach((card) => {
    const bucket = card[statusField] || columns[0]?.name;
    if (!cardsByColumn[bucket]) cardsByColumn[bucket] = [];
    cardsByColumn[bucket].push(card);
  });

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const { source, destination, draggableId } = result;
    if (source.droppableId === destination.droppableId) return;
    const targetColName = destination.droppableId;
    try {
      await onStatusChange(draggableId, targetColName);
      toast.success(`Moved to ${targetColName}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to move card");
    }
  };

  const addColumn = async () => {
    if (!newColName.trim()) return;
    try {
      const { data } = await api.post(`/board-columns/${boardKey}`, { name: newColName.trim() });
      setColumns([...columns, data].sort((a, b) => (a.order || 0) - (b.order || 0)));
      setNewColName("");
      setAdding(false);
      toast.success("Column added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add column");
    }
  };

  const renameColumn = async (col) => {
    if (!editName.trim() || editName === col.name) { setEditingColId(null); return; }
    try {
      await api.put(`/board-columns/${boardKey}/${col.id}`, { name: editName.trim() });
      setColumns(columns.map(c => c.id === col.id ? { ...c, name: editName.trim() } : c));
      setEditingColId(null);
      toast.success("Column renamed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to rename");
    }
  };

  const deleteColumn = async (col) => {
    if (col.system) { toast.error("Cannot delete a default column"); return; }
    if (!window.confirm(`Delete column "${col.name}"? Cards in this column will move to New.`)) return;
    try {
      await api.delete(`/board-columns/${boardKey}/${col.id}`);
      setColumns(columns.filter(c => c.id !== col.id));
      toast.success("Column deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex gap-3 overflow-x-auto pb-2 items-start" data-testid={`kanban-${boardKey}`}>
        {columns.map((col) => {
          const colCards = cardsByColumn[col.name] || [];
          return (
            <div key={col.id} className="min-w-[260px] max-w-[300px] flex-1 bg-slate-50 rounded-xl border border-slate-200 flex flex-col">
              <div className="px-3 py-2.5 border-b border-slate-200 flex items-center justify-between">
                {editingColId === col.id ? (
                  <div className="flex items-center gap-1 flex-1">
                    <Input
                      data-testid={`rename-col-input-${col.id}`}
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onBlur={() => renameColumn(col)}
                      onKeyDown={(e) => e.key === "Enter" && renameColumn(col)}
                      autoFocus
                      className="h-7 text-sm"
                    />
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-sm text-slate-700" data-testid={`col-title-${col.id}`}>{col.name}</h3>
                      <span className="text-[10px] bg-white text-slate-500 rounded-full px-2 py-0.5 border border-slate-200">
                        {colCards.length}
                      </span>
                    </div>
                    {!col.system && (
                      <div className="flex items-center gap-0.5">
                        <button
                          data-testid={`rename-col-${col.id}`}
                          onClick={() => { setEditingColId(col.id); setEditName(col.name); }}
                          className="text-slate-400 hover:text-teal-600 p-1"
                          title="Rename"
                        >
                          <Edit2 size={12} />
                        </button>
                        <button
                          data-testid={`delete-col-${col.id}`}
                          onClick={() => deleteColumn(col)}
                          className="text-slate-400 hover:text-red-500 p-1"
                          title="Delete"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              <Droppable droppableId={col.name}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`flex-1 p-2 min-h-[200px] space-y-2 transition-colors ${snapshot.isDraggingOver ? "bg-teal-50" : ""}`}
                  >
                    {colCards.length === 0 && !snapshot.isDraggingOver && (
                      <p className="text-[11px] text-slate-400 text-center py-6">Drop cards here</p>
                    )}
                    {colCards.map((card, idx) => (
                      <Draggable key={card.id} draggableId={card.id} index={idx}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            data-testid={`kanban-card-${card.id}`}
                            onClick={() => onCardClick(card)}
                            className={`bg-white rounded-lg border border-slate-200 p-3 cursor-pointer hover:border-teal-400 hover:shadow-sm transition-all ${snapshot.isDragging ? "shadow-lg ring-2 ring-teal-400" : ""}`}
                          >
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                              <h4 className="text-sm font-semibold text-teal-700 leading-tight" data-testid={`card-jc-${card.id}`}>
                                {card.job_card_number || "-"}
                              </h4>
                              {card.unread > 0 && (
                                <span
                                  data-testid={`card-unread-${card.id}`}
                                  className="bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0"
                                  title="New activity"
                                >
                                  !
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-600 leading-tight line-clamp-1">{card.model || "-"}</p>
                            <p className="text-[11px] text-slate-400 line-clamp-1">{card.store || "-"}</p>
                            <div className="flex items-center gap-2 mt-2 text-[10px] text-slate-400">
                              <Calendar size={10} /> {formatDate(card.date_completed)}
                              {(card.accounting_notes || []).length > 0 && (
                                <span className="flex items-center gap-1 ml-auto">
                                  <MessageSquare size={10} />
                                  {(card.accounting_notes || []).length}
                                </span>
                              )}
                            </div>
                            {card.completion_report_url && (
                              <div className="flex gap-1 mt-2 pt-2 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
                                <a
                                  href={`${API_URL}/api/files/${card.completion_report_url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-testid={`card-print-${card.id}`}
                                  className="inline-flex items-center gap-1 text-[10px] text-teal-600 hover:text-teal-800 font-medium"
                                >
                                  <Printer size={10} /> Print
                                </a>
                                <a
                                  href={`${API_URL}/api/files/${card.completion_report_url}`}
                                  download={`${card.job_card_number}-completion-report.pdf`}
                                  data-testid={`card-download-${card.id}`}
                                  className="inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-800 font-medium ml-2"
                                >
                                  <Download size={10} /> PDF
                                </a>
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}

        {canAddColumn && (
          <div className="min-w-[220px]">
            {adding ? (
              <div className="bg-white rounded-xl border border-slate-200 p-3">
                <Input
                  data-testid="new-col-input"
                  value={newColName}
                  onChange={(e) => setNewColName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addColumn()}
                  placeholder="Column name"
                  autoFocus
                  className="h-8 text-sm mb-2"
                />
                <div className="flex gap-1">
                  <Button data-testid="save-new-col" onClick={addColumn} size="sm" className="bg-teal-600 hover:bg-teal-700 text-xs h-7">Add</Button>
                  <Button onClick={() => { setAdding(false); setNewColName(""); }} variant="ghost" size="sm" className="text-xs h-7">Cancel</Button>
                </div>
              </div>
            ) : (
              <button
                data-testid="add-col-btn"
                onClick={() => setAdding(true)}
                className="w-full bg-slate-50 hover:bg-slate-100 border border-dashed border-slate-300 rounded-xl p-3 text-sm text-slate-500 hover:text-teal-600 flex items-center justify-center gap-2 transition-colors"
              >
                <Plus size={14} /> Add Column
              </button>
            )}
          </div>
        )}
      </div>

      {cards.length === 0 && emptyHint && (
        <div className="mt-6 text-center text-sm text-slate-400 flex items-center justify-center gap-2">
          <FileText size={14} /> {emptyHint}
        </div>
      )}
    </DragDropContext>
  );
}
